import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

// Get current month key for usage tracking
function getCurrentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)
  const [subscription, setSubscription] = useState({ tier: 'free', status: 'free', usage: null, limits: null })

  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async load(userId) {
      if (!userId) return
      setProfileLoading(true)
      try {
        // Fetch profile, usage, and limits in parallel
        const month = getCurrentMonth()
        const [profileRes, usageRes, limitsRes] = await Promise.all([
          supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.maybeSingle(),
          supabase?.from('user_usage')?.select('*')?.eq('user_id', userId)?.eq('month', month)?.maybeSingle(),
          supabase?.from('plan_limits')?.select('*'),
        ])

        if (!profileRes?.error) setUserProfile(profileRes?.data)

        // Build subscription state
        const tier = profileRes?.data?.subscription_tier || 'free'
        const tierLimits = limitsRes?.data?.find(l => l.tier === tier) || null
        const usage = usageRes?.data || { ai_generations: 0, publishes: 0, image_uploads: 0 }

        // Calculate days until reset
        const now = new Date()
        const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1)
        const daysUntilReset = Math.ceil((nextMonth - now) / (1000 * 60 * 60 * 24))

        setSubscription({
          tier,
          status: profileRes?.data?.subscription_status || 'free',
          usage,
          limits: tierLimits,
          daysUntilReset,
        })
      } catch (error) {
        console.error('Profile load error:', error?.message)
      } finally {
        setProfileLoading(false)
      }
    },

    clear() {
      setUserProfile(null)
      setSubscription({ tier: 'free', status: 'free', usage: null, limits: null })
      setProfileLoading(false)
    }
  }

  // Reload subscription state (profile tier + usage + limits).
  // Pass `optimistic` (e.g. { ai_generations: 1 }) to bump counters locally
  // before the server fetch lands — keeps the sidebar in sync with what the
  // user just did even if the network round-trip is slow.
  const refreshUsage = async (optimistic = null) => {
    if (!user?.id) return
    if (optimistic && typeof optimistic === 'object') {
      setSubscription(prev => {
        const prevUsage = prev?.usage || { ai_generations: 0, publishes: 0, image_uploads: 0, scheduled_posts: 0 }
        const nextUsage = { ...prevUsage }
        for (const [key, delta] of Object.entries(optimistic)) {
          nextUsage[key] = (prevUsage[key] || 0) + delta
        }
        return { ...prev, usage: nextUsage }
      })
    }
    await profileOperations.load(user.id)
  }

  // Auth state handlers - PROTECTED from async modification
  const authStateHandlers = {
    // This handler MUST remain synchronous - Supabase requirement
    onChange: (event, session) => {
      setUser(session?.user ?? null)
      setLoading(false)
      
      if (session?.user) {
        profileOperations?.load(session?.user?.id) // Fire-and-forget
      } else {
        profileOperations?.clear()
      }
    }
  }

  useEffect(() => {
    // Initial session check
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      authStateHandlers?.onChange(null, session)
    })

    // CRITICAL: This must remain synchronous
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    )

    return () => subscription?.unsubscribe()
  }, [])

  // Auth methods
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signUp = async (email, password, fullName) => {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            provider: 'email',
          },
        },
      })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut()
      if (!error) {
        setUser(null)
        profileOperations?.clear()
        sessionStorage.removeItem('onboarding_dismissed')
      }
      return { error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signInWithGoogle = async () => {
    try {
      const { data, error } = await supabase?.auth?.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location?.origin}/login`,
          skipBrowserRedirect: false,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      })
      
      if (error) {
        console.error('[AuthContext] OAuth error:', error?.message);
      }

      return { data, error }
    } catch (error) {
      console.error('[AuthContext] OAuth error:', error?.message);
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  // Fields users are allowed to update from the client.
  // Subscription/payment fields must only be written by server (webhooks via service role).
  const ALLOWED_PROFILE_FIELDS = [
    'full_name', 'github_url', 'avatar_url', 'default_writing_style',
  ]

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }

    try {
      // If no updates provided, just fetch current profile
      if (!updates || Object.keys(updates).length === 0) {
        const { data, error } = await supabase?.from('user_profiles')?.select()?.eq('id', user?.id)?.single()
        if (!error) setUserProfile(data)
        return { data, error }
      }

      // Filter out any fields that shouldn't be client-updatable
      const safeUpdates = {}
      for (const key of Object.keys(updates)) {
        if (ALLOWED_PROFILE_FIELDS.includes(key)) {
          safeUpdates[key] = updates[key]
        } else {
          console.warn(`[AuthContext] Blocked update to restricted field: ${key}`)
        }
      }

      if (Object.keys(safeUpdates).length === 0) {
        // All fields were blocked — just refresh profile
        const { data, error } = await supabase?.from('user_profiles')?.select()?.eq('id', user?.id)?.single()
        if (!error) setUserProfile(data)
        return { data, error }
      }

      const { data, error } = await supabase?.from('user_profiles')?.update(safeUpdates)?.eq('id', user?.id)?.select()?.single()
      if (!error) setUserProfile(data)
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signUp,
    signOut,
    signInWithGoogle,
    updateProfile,
    subscription,
    refreshUsage,
    isAuthenticated: !!user
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
